import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { Card, CommonCss, PageContainer, PageTitle } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme } from 'styles/theme';
import { ActionButtonGroup, CollapsibleSearchPanel, FormArea } from 'components/forms';
import { HiGridComponent, hiCol, useHiGridHook } from 'components/grid/hiGrid';
import {
  ActiveGridHeader,
  ActiveGridProvider,
  ActiveGridScope,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import {
  GridExportFocusScope,
  GridExportProvider,
  useExcelExportAction,
  useRegisterGridExport,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  MODULE_OPTIONS,
  YN_OPTIONS,
  filterCodeGroups,
  loadCodeGroups,
  loadCodes,
  resolveCodeDepth,
  saveCodeGroups,
  saveCodes,
  validateCodeGroups,
  validateCodes,
} from 'master/commonCodeStore';

const GROUP_GRID_ID = 'cm-code-group';
const CODE_GRID_ID = 'cm-code-detail';

const Meta = styled.div`
  margin-bottom: 4px;
  flex-shrink: 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.xs};
`;

const Stack = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
`;

const Panel = styled(Card)`
  display: flex;
  flex: ${(p) => p.$flex || 1};
  flex-direction: column;
  min-height: ${(p) => p.$minH || '0'};
  padding: 0;
  overflow: hidden;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

function hasDirty(rows) {
  return (rows || []).some((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'));
}

const CommonCodeManageInner = () => {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const groupGridRef = useRef(null);
  const codeGridRef = useRef(null);
  const selectedGroupRef = useRef('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [parentTick, setParentTick] = useState(0);
  const codesCacheRef = useRef(new Map());

  const { handleActiveAdd, handleActiveDelete, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (id === CODE_GRID_ID && !selectedGroupRef.current) {
        return '코드그룹을 먼저 선택하세요.';
      }
      return null;
    },
  });

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      codeGroup: '',
      codeGroupNm: '',
      moduleCd: '',
      useYn: 'Y',
    },
  });
  const { getValues } = methods;

  const groupColumns = useMemo(
    () => [
      hiCol.edTextColumnForKey({
        header: '그룹ID',
        field: 'codeGroup',
        size: 140,
        required: true,
        title: '최초 저장 후 변경 불가',
      }),
      hiCol.edTextColumn({
        header: '그룹명',
        field: 'codeGroupNm',
        size: 160,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '모듈',
        field: 'moduleCd',
        size: 100,
        options: MODULE_OPTIONS,
      }),
      hiCol.edComboColumn({
        header: '시스템',
        field: 'systemYn',
        size: 80,
        options: YN_OPTIONS,
        title: 'Y면 삭제 제한',
      }),
      hiCol.edComboColumn({
        header: '사용',
        field: 'useYn',
        size: 80,
        options: YN_OPTIONS,
      }),
      hiCol.edNumberColumn({
        header: '정렬',
        field: 'sortOrder',
        size: 80,
        align: 'center',
      }),
      hiCol.edTextColumn({ header: '설명', field: 'description', size: 240 }),
    ],
    []
  );

  const parentOptions = useMemo(() => {
    const rows =
      codesCacheRef.current.get(selectedGroup) || codeGridRef.current?.getData?.() || [];
    const opts = [{ value: '', label: '(루트)' }];
    rows
      .filter((r) => r && r.mod !== 'D' && r.code)
      .forEach((r) => {
        opts.push({ value: String(r.code), label: `${r.code} ${r.codeNm || ''}`.trim() });
      });
    return opts;
    // parentTick: 상세 로드·셀 변경 후 콤보 옵션 갱신
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, parentTick]);

  const codeColumns = useMemo(
    () => [
      hiCol.edTextColumnForKey({
        header: '코드',
        field: 'code',
        size: 120,
        required: true,
        title: '최초 저장 후 변경 불가',
      }),
      hiCol.edTextColumn({
        header: '코드명',
        field: 'codeNm',
        size: 140,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '상위코드',
        field: 'parentCode',
        size: 140,
        options: parentOptions,
        title: '동일 그룹 내 상위 (빈 값=루트)',
      }),
      hiCol.roTextColumn({
        header: '깊이',
        field: 'depth',
        size: 70,
        align: 'center',
        title: '상위코드 기준 자동 계산',
      }),
      hiCol.edComboColumn({
        header: '사용',
        field: 'useYn',
        size: 80,
        options: YN_OPTIONS,
      }),
      hiCol.edNumberColumn({
        header: '정렬',
        field: 'sortOrder',
        size: 80,
        align: 'center',
      }),
      hiCol.edTextColumn({ header: '속성1', field: 'attr1', size: 100, title: '예: 숫자 레벨' }),
      hiCol.edTextColumn({ header: '속성2', field: 'attr2', size: 100 }),
      hiCol.edTextColumn({ header: '속성3', field: 'attr3', size: 100 }),
      hiCol.edTextColumn({ header: '영문명', field: 'codeNmEn', size: 120 }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 180 }),
    ],
    [parentOptions]
  );

  const groupGrid = useHiGridHook({
    gridRef: groupGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      codeGroup: '',
      codeGroupNm: '',
      moduleCd: 'COMMON',
      systemYn: 'N',
      useYn: 'Y',
      sortOrder: 10,
      description: '',
    },
  });

  const codeGrid = useHiGridHook({
    gridRef: codeGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      codeGroup: '',
      code: '',
      codeNm: '',
      codeNmEn: '',
      parentCode: '',
      depth: 1,
      sortOrder: 10,
      attr1: '',
      attr2: '',
      attr3: '',
      useYn: 'Y',
      remark: '',
    },
  });

  const stashCodes = useCallback(() => {
    const g = selectedGroupRef.current;
    if (!g) return;
    codesCacheRef.current.set(g, codeGridRef.current?.getData?.() || []);
  }, []);

  const showCodesForGroup = useCallback(
    async (codeGroup, { forceReload = false } = {}) => {
      const g = String(codeGroup || '').trim();
      selectedGroupRef.current = g;
      setSelectedGroup(g);
      if (!g) {
        codeGridRef.current?.setData?.([]);
        return;
      }
      if (!forceReload && codesCacheRef.current.has(g)) {
        codeGridRef.current?.setData?.(codesCacheRef.current.get(g) || []);
        setParentTick((t) => t + 1);
        return;
      }
      try {
        const rows = await loadCodes({ codeGroup: g });
        codesCacheRef.current.set(g, rows);
        codeGridRef.current?.setData?.(rows);
        setParentTick((t) => t + 1);
      } catch (e) {
        showAlert(e?.message || '상세 코드 조회에 실패했습니다.', 'error');
      }
    },
    []
  );

  const handleSearch = useCallback(async () => {
    try {
      stashCodes();
      const all = await loadCodeGroups({});
      const filtered = filterCodeGroups(all, getValues());
      groupGridRef.current?.setData?.(filtered);

      const still = filtered.find((r) => r.codeGroup === selectedGroupRef.current);
      if (still) {
        await showCodesForGroup(still.codeGroup, { forceReload: true });
      } else {
        selectedGroupRef.current = '';
        setSelectedGroup('');
        codeGridRef.current?.setData?.([]);
      }
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [getValues, showCodesForGroup, stashCodes]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  const handleSelectGroup = useCallback(
    async (row) => {
      const next = String(row?.codeGroup || '').trim();
      if (!next || next === selectedGroupRef.current) return;
      stashCodes();
      await showCodesForGroup(next);
    },
    [showCodesForGroup, stashCodes]
  );

  const handleAddGroup = useCallback(async () => {
    const rows = groupGridRef.current?.getData?.() || [];
    const maxSort = rows.reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0);
    await groupGrid.handleAdd({
      codeGroup: '',
      codeGroupNm: '',
      moduleCd: 'COMMON',
      systemYn: 'N',
      useYn: 'Y',
      sortOrder: Math.ceil((maxSort + 1) / 10) * 10,
      description: '',
    });
  }, [groupGrid]);

  const handleAddCode = useCallback(async () => {
    const g = selectedGroupRef.current;
    if (!g) {
      showAlert('코드그룹을 먼저 선택하세요.', 'warning');
      return;
    }
    const rows = codeGridRef.current?.getData?.() || [];
    const maxSort = rows.reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0);
    await codeGrid.handleAdd({
      codeGroup: g,
      code: '',
      codeNm: '',
      codeNmEn: '',
      parentCode: '',
      depth: 1,
      sortOrder: Math.ceil((maxSort + 1) / 10) * 10,
      attr1: '',
      attr2: '',
      attr3: '',
      useYn: 'Y',
      remark: '',
    });
  }, [codeGrid]);

  const handleDeleteGroup = useCallback(async () => {
    await groupGrid.handleDelete();
  }, [groupGrid]);

  const handleDeleteCode = useCallback(async () => {
    await codeGrid.handleDelete();
  }, [codeGrid]);

  const handleAfterSaveCell = useCallback((row, { field } = {}) => {
    if (!row || (field !== 'parentCode' && field !== 'code')) return;
    const rows = codeGridRef.current?.getData?.() || [];
    const idx = rows.findIndex(
      (r) => r === row || (r.code && row.code && String(r.code) === String(row.code) && r.mod === row.mod)
    );
    if (idx < 0) return;
    const next = [...rows];
    const cur = { ...next[idx] };
    cur.depth = resolveCodeDepth(cur, next);
    if (cur.mod !== 'A') cur.mod = 'U';
    next[idx] = cur;
    codeGridRef.current?.setData?.(next);
  }, []);

  useEffect(() => {
    groupGrid.onRowClicked = ({ data }) => {
      handleSelectGroup(data);
    };
    groupGrid.onSelectRow = (row) => {
      handleSelectGroup(row);
    };
  }, [groupGrid, handleSelectGroup]);

  useEffect(() => {
    codeGrid.onAfterSaveCell = (row, cell) => {
      handleAfterSaveCell(row, cell);
      stashCodes();
      setParentTick((t) => t + 1);
    };
  }, [codeGrid, handleAfterSaveCell, stashCodes]);

  const handleSave = useCallback(async () => {
    try {
      stashCodes();
      const groupRows = groupGridRef.current?.getData?.() || [];
      const gVal = validateCodeGroups(groupRows);
      if (!gVal.ok) {
        showAlert(gVal.message, 'warning');
        return;
      }

      // 삭제 예정 그룹: 상세 존재 여부 검사
      for (const r of groupRows) {
        if (r?.mod !== 'D') continue;
        const g = String(r.codeGroup || '').trim();
        let detail = codesCacheRef.current.get(g);
        if (!detail) {
          detail = await loadCodes({ codeGroup: g });
        }
        const v = validateCodes(detail, { deletingGroup: true });
        if (!v.ok) {
          showAlert(v.message, 'warning');
          return;
        }
      }

      for (const [g, detail] of codesCacheRef.current.entries()) {
        const v = validateCodes(detail);
        if (!v.ok) {
          showAlert(`[${g}] ${v.message}`, 'warning');
          return;
        }
      }

      // 저장 순서: 상세 삭제 → 그룹 A/U/D → 상세 A/U (FK: code → group)
      const allDetailDirty = [];
      for (const [g, detail] of codesCacheRef.current.entries()) {
        for (const r of detail || []) {
          if (!r || (r.mod !== 'A' && r.mod !== 'U' && r.mod !== 'D')) continue;
          allDetailDirty.push({ ...r, codeGroup: r.codeGroup || g });
        }
      }
      const detailDeletes = allDetailDirty.filter((r) => r.mod === 'D');
      const detailUpserts = allDetailDirty.filter((r) => r.mod === 'A' || r.mod === 'U');

      if (detailDeletes.length) {
        await saveCodes(detailDeletes);
      }

      const dirtyGroups = groupRows.filter(
        (r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D')
      );
      if (dirtyGroups.length) {
        await saveCodeGroups(dirtyGroups);
      }

      if (detailUpserts.length) {
        await saveCodes(detailUpserts);
      }

      codesCacheRef.current.clear();
      await handleSearch();
      showAlert('저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [handleSearch, stashCodes]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'codeGroup',
          label: '그룹ID',
          type: 'text',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'codeGroupNm',
          label: '그룹명',
          type: 'text',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'moduleCd',
          label: '모듈',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...MODULE_OPTIONS],
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          width: CommonCss.w70,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
        },
      ],
    ],
    []
  );

  useRegisterGridExport({
    id: GROUP_GRID_ID,
    label: '코드그룹',
    getData: () =>
      groupGridRef.current?.getFilteredData?.() ?? groupGridRef.current?.getData?.() ?? [],
    getDataColumns: () => groupGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['codeGroup'],
    order: 0,
  });
  useRegisterGridExport({
    id: CODE_GRID_ID,
    label: '코드상세',
    getData: () =>
      codeGridRef.current?.getFilteredData?.() ?? codeGridRef.current?.getData?.() ?? [],
    getDataColumns: () => codeGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['codeGroup', 'code'],
    order: 1,
  });
  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '공통코드',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleActiveAdd, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, openExcelDialog]
  );

  // ActiveGridScope 에 실제 add/delete 연결
  const groupAdd = handleAddGroup;
  const groupDel = handleDeleteGroup;
  const codeAdd = handleAddCode;
  const codeDel = handleDeleteCode;

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>공통코드</PageTitle>
        <Meta>시스템 마스터 전용 화면입니다.</Meta>
      </PageContainer>
    );
  }

  if (!isBdApiEnabled()) {
    return (
      <PageContainer>
        <PageTitle>공통코드</PageTitle>
        <Meta>API 모드에서만 사용 가능합니다. (REACT_APP_USE_BD_API)</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="cm-common-code-search"
          prefsUserId={prefsUserId}
          title="조회조건"
        >
          <FormArea fields={searchFields} compact defaultValues={getValues()} />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        그룹을 선택하면 하단에 상세 코드가 표시됩니다. 상세는 동일 그룹 내 상위코드로 계층을 둘 수
        있습니다. Alt+F2로 그리드 전환 · 미저장 상세는 그룹 전환 시 임시 보관됩니다.
        {selectedGroup ? ` · 선택그룹: ${selectedGroup}` : ''}
        {hasDirty(groupGridRef.current?.getData?.()) ||
        hasDirty(codeGridRef.current?.getData?.())
          ? ' · 변경사항 있음'
          : ''}
      </Meta>

      <Stack>
        <Panel $flex="0 0 38%" $minH="200px">
          <ActiveGridHeader gridId={GROUP_GRID_ID}>코드그룹</ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={GROUP_GRID_ID}
              label="코드그룹"
              order={0}
              onAdd={groupAdd}
              onDelete={groupDel}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={GROUP_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={groupGridRef}
                  columns={groupColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  enableColumnFilters
                  enableContextMenu
                  enableRowEditMenu
                  enableCut
                  enableFindReplace
                  allowReplace
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="공통코드그룹"
                  gridId={GROUP_GRID_ID}
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === GROUP_GRID_ID}
                  {...groupGrid.contextMenuProps}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={1} $minH="240px">
          <ActiveGridHeader
            gridId={CODE_GRID_ID}
            extra={selectedGroup ? `그룹: ${selectedGroup}` : '그룹 미선택'}
          >
            코드상세
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={CODE_GRID_ID}
              label="코드상세"
              order={1}
              onAdd={codeAdd}
              onDelete={codeDel}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={CODE_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={codeGridRef}
                  columns={codeColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  enableColumnFilters
                  enableContextMenu
                  enableRowEditMenu
                  enableCut
                  enableFindReplace
                  allowReplace
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="공통코드상세"
                  gridId={CODE_GRID_ID}
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === CODE_GRID_ID}
                  {...codeGrid.contextMenuProps}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>
      {excelDialog}
    </PageContainer>
  );
};

const CommonCodeManage = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <CommonCodeManageInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default CommonCodeManage;
